// Avatar with a dropdown of profile + sign-out actions, ported from
// careers.co.jp's shell. Bridges Clerk (real auth) and the dev bypass.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar } from "@/components/ui/avatar";
import { DEV_AUTH_ENABLED, clearDevAuth, getDevAuth } from "@/lib/devAuth";
import { displayNameFromEmail } from "@/lib/jobs";
import { cn } from "@/lib/utils";

function useSignOut(): { email: string | null; signOut: () => Promise<void> } {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Clerk's hook only works inside ClerkProvider; in dev mode there is no
  // provider, so branch on the build-time constant (stable per bundle).
  if (DEV_AUTH_ENABLED) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const auth = getDevAuth();
    return {
      email: auth?.email ?? null,
      signOut: async () => {
        // The query cache belongs to the old identity — wipe it so the next
        // user doesn't see stale data on first paint.
        qc.clear();
        clearDevAuth();
        navigate("/sign-in");
      },
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const clerk = useClerk();
  return {
    email: clerk.user?.primaryEmailAddress?.emailAddress ?? null,
    signOut: async () => {
      qc.clear();
      await clerk.signOut({ redirectUrl: "/sign-in" });
    },
  };
}

export function AvatarMenu() {
  const { email, signOut } = useSignOut();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const name = displayNameFromEmail(email) ?? "Account";

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Open account menu"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full p-0.5 pr-1.5 hover:bg-surface-2",
          open && "bg-surface-2",
        )}
      >
        <Avatar alt={name} size="sm" variant="brand" />
        <ChevronDown className="h-3.5 w-3.5 text-text-2" strokeWidth={2} aria-hidden />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-56 animate-fade-in overflow-hidden rounded-md border border-border bg-surface shadow-pop"
        >
          <div className="border-b border-border px-4 py-3">
            <p className="truncate text-sm font-medium text-text">{name}</p>
            {email ? <p className="truncate text-xs text-text-2">{email}</p> : null}
          </div>
          <ul className="py-1.5">
            <li>
              <Link
                to="/profile"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-4 py-2 text-sm text-text no-underline hover:bg-surface-2"
              >
                <UserIcon className="h-4 w-4 text-text-2" strokeWidth={1.75} aria-hidden />
                My profile
              </Link>
            </li>
            <li>
              <button
                type="button"
                role="menuitem"
                onClick={() => void signOut()}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-text hover:bg-surface-2"
              >
                <LogOut className="h-4 w-4 text-text-2" strokeWidth={1.75} aria-hidden />
                Sign out
              </button>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}
